import React, { useState, useEffect, useRef } from 'react';
import { UnitsOfLength } from '@race-planner/aws-amplify/src/models';
import isUndefined from 'lodash/isUndefined';
import last from 'lodash/last';
import find from 'lodash/find';
import constant from 'lodash/constant';
import { usePrevious } from '@codexporer.io/react-hooks';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import {
    convertMetersToMiles,
    convertMetersToKilometers,
    convertMilesToMeters,
    convertKilometersToMeters,
    MARATHON_DISTANCE_METERS,
    HALF_MARATHON_DISTANCE_METERS
} from '@race-planner/unit-converters';
import { NumberInput } from '../number-input';

const selectOptions = [
    {
        text: '5K',
        key: '5K',
        distance: convertKilometersToMeters(5)
    },
    {
        text: '10K',
        key: '10K',
        distance: convertKilometersToMeters(10)
    },
    {
        text: 'Half marathon',
        key: 'Half marathon',
        distance: HALF_MARATHON_DISTANCE_METERS
    },
    {
        text: 'Marathon',
        key: 'Marathon',
        distance: MARATHON_DISTANCE_METERS
    },
    {
        text: 'Custom',
        key: 'Custom',
        isCustom: true
    }
];

const getDisplayValue = {
    [UnitsOfLength.METRIC]: ({ value, shouldUseMeters }) => {
        if (shouldUseMeters) {
            return value;
        }

        return convertMetersToKilometers(value);
    },
    [UnitsOfLength.STATUTE]: ({ value }) => convertMetersToMiles(value)
};

const getDistanceValue = {
    [UnitsOfLength.METRIC]: ({ value, shouldUseMeters }) => {
        const numValue = +value;
        if (shouldUseMeters) {
            return numValue;
        }

        return convertKilometersToMeters(numValue);
    },
    [UnitsOfLength.STATUTE]: ({ value }) => convertMilesToMeters(+value)
};

const unit = {
    [UnitsOfLength.METRIC]: ({ shouldUseMeters }) => shouldUseMeters ? 'm' : 'km',
    [UnitsOfLength.STATUTE]: constant('mi')
};

export const DistanceInput = ({
    value,
    onValueChange,
    isAutoSelection,
    disabled,
    shouldHideSelector,
    shouldUseMeters,
    inputLabel,
    unitsOfLength
}) => {
    const [selectedOption, setSelectedOption] = useState(
        !shouldHideSelector && (
            value ?
                (
                    find(selectOptions, { distance: value }) ||
                    last(selectOptions)
                ) :
                selectOptions[0]
        ) || last(selectOptions)
    );
    const [displayValue, setDisplayValue] = useState(
        `${getDisplayValue[unitsOfLength]({ value, shouldUseMeters })}`
    );
    const onValueChangeRef = useRef();
    onValueChangeRef.current = onValueChange;

    const previousShouldHideSelector = usePrevious(shouldHideSelector);
    useEffect(() => {
        if (shouldHideSelector && !previousShouldHideSelector) {
            setSelectedOption(last(selectOptions));
        }
    }, [
        setSelectedOption,
        previousShouldHideSelector,
        shouldHideSelector
    ]);

    const previousSelectedOption = usePrevious(selectedOption);
    useEffect(
        () => {
            previousSelectedOption && onValueChangeRef.current?.(
                selectedOption.distance ||
                getDistanceValue[unitsOfLength]({ value: displayValue, shouldUseMeters })
            );
        },
        [
            previousSelectedOption,
            selectedOption.distance,
            displayValue,
            shouldUseMeters,
            unitsOfLength
        ]
    );

    const previousValue = usePrevious(value);
    const previousDisplayValue = usePrevious(displayValue);
    useEffect(
        () => {
            if (isUndefined(previousValue) ||
                previousValue === value ||
                isUndefined(previousDisplayValue) ||
                previousDisplayValue !== displayValue) {
                return;
            }

            setDisplayValue(
                `${getDisplayValue[unitsOfLength]({ value, shouldUseMeters })}`
            );
        },
        [
            previousValue,
            value,
            previousDisplayValue,
            displayValue,
            setDisplayValue,
            shouldUseMeters,
            unitsOfLength
        ]
    );

    useEffect(
        () => {
            if (previousValue === value) {
                return;
            }

            !shouldHideSelector && isAutoSelection && setSelectedOption(
                find(selectOptions, { distance: value }) ||
                last(selectOptions)
            );
        },
        [isAutoSelection, previousValue, shouldHideSelector, value]
    );

    const hendleDistanceChange = (event, option) => {
        setSelectedOption(option);
    };

    const handleValueChange = (event, value) => {
        setDisplayValue(value);
    };

    return (
        <div>
            {!shouldHideSelector && (
                <Dropdown
                    label='Distance'
                    selectedKey={selectedOption.key}
                    onChange={hendleDistanceChange}
                    options={selectOptions}
                    disabled={disabled}
                />
            )}
            {selectedOption.isCustom && (
                <NumberInput
                    label={
                        inputLabel ??
                        `Enter distance (${unit[unitsOfLength]({ shouldUseMeters })})`
                    }
                    value={displayValue}
                    onChange={handleValueChange}
                    disabled={disabled}
                />
            )}
        </div>
    );
};
